import React, { useState, useEffect, useRef, useMemo } from 'react';
import { datosGeneralesService } from '../services/api';

/* ── Claves localStorage ── */
const LS = {
  pe_recepciona: 'imprimir_pe_recepciona',
  ps_autoriza:   'imprimir_ps_autoriza',
  ps_despacha:   'imprimir_ps_despacha',
};

const loadLS = (key, trabajadores) => {
  const id = localStorage.getItem(key);
  if (!id) return null;
  return trabajadores.find(t => String(t.id_trabajador) === id) || null;
};

/* ── Mini autocomplete para firma ── */
const FirmaAC = ({ label, trabajadores, value, onChange, canDefault, lsKey }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? trabajadores.filter(t => t.nombres.toLowerCase().includes(q) || (t.dni || '').includes(q))
      : trabajadores;
    return list.slice(0, 20);
  }, [query, trabajadores]);

  const select = t => { onChange(t); setQuery(''); setOpen(false); };
  const clear  = ()  => { onChange(null); setQuery(''); };

  const isDefault = canDefault && value && localStorage.getItem(lsKey) === String(value.id_trabajador);

  const saveDefault = e => {
    e.stopPropagation();
    if (value) localStorage.setItem(lsKey, String(value.id_trabajador));
    else localStorage.removeItem(lsKey);
  };

  const inputStyle = {
    width: '100%', height: 32, padding: '0 10px', fontSize: 12.5, boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 7, color: '#e2e8f0', outline: 'none', fontFamily: 'Inter, sans-serif',
  };

  return (
    <div ref={ref} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
        <label style={{ fontSize: 10.5, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
          {label}
        </label>
        {canDefault && value && (
          <button onClick={saveDefault} title={isDefault ? 'Valor predeterminado guardado' : 'Guardar como predeterminado'}
            style={{
              fontSize: 10, padding: '1px 7px', borderRadius: 5, border: '1px solid', cursor: 'pointer',
              background: isDefault ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.05)',
              color: isDefault ? '#22c55e' : '#64748b',
              borderColor: isDefault ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.1)',
              fontFamily: 'Inter, sans-serif', fontWeight: 600, transition: 'all 0.15s',
            }}>
            {isDefault ? '★ Predeterminado' : '☆ Guardar por defecto'}
          </button>
        )}
      </div>

      {value ? (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.3)',
          borderRadius: 7, padding: '5px 10px', minHeight: 32,
        }}>
          <div>
            <div style={{ fontSize: 12.5, color: '#e2e8f0', fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>{value.nombres}</div>
            {value.dni && <div style={{ fontSize: 10.5, color: '#64748b', fontFamily: 'Inter, sans-serif' }}>DNI: {value.dni}</div>}
          </div>
          <button onClick={clear} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#60a5fa', fontSize: 17, lineHeight: 1, padding: '0 2px' }}>×</button>
        </div>
      ) : (
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Buscar por nombre o DNI..."
          style={inputStyle}
          onFocus={e => { setOpen(true); e.target.style.borderColor = '#3b82f6'; }}
          onBlur={e  => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
        />
      )}

      {open && !value && filtered.length > 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 2000,
          background: '#0d1a2e', border: '1px solid rgba(96,165,250,0.2)', borderRadius: 9,
          boxShadow: '0 12px 32px rgba(0,0,0,0.7)', maxHeight: 200, overflowY: 'auto',
        }}>
          {filtered.map(t => (
            <div key={t.id_trabajador} onMouseDown={() => select(t)}
              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(96,165,250,0.09)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 500, fontFamily: 'Inter, sans-serif' }}>{t.nombres}</div>
              {t.dni && <div style={{ fontSize: 10.5, color: '#64748b', fontFamily: 'Inter, sans-serif' }}>DNI: {t.dni}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Bloque de firma para impresión ── */
const FirmaBox = ({ titulo, trabajador }) => (
  <div style={{ flex: 1, textAlign: 'center', padding: '0 18px' }}>
    <div style={{ marginTop: 52, borderBottom: '1.5px solid #37474f', marginBottom: 5 }} />
    <div style={{ fontSize: 10.5, fontWeight: 700, color: '#1a1a2e', fontFamily: '"Segoe UI", Arial, sans-serif', minHeight: 15 }}>
      {trabajador?.nombres || ''}
    </div>
    {trabajador?.dni && (
      <div style={{ fontSize: 9.5, color: '#555', fontFamily: '"Segoe UI", Arial, sans-serif' }}>DNI: {trabajador.dni}</div>
    )}
    <div style={{ fontSize: 9, color: '#37474f', fontFamily: '"Segoe UI", Arial, sans-serif', marginTop: 3, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700 }}>
      {titulo}
    </div>
  </div>
);

/* ════════════════════════════════════════════════════════════════
   MODAL PRINCIPAL
════════════════════════════════════════════════════════════════ */
export default function ImprimirParteModal({ tipo, parte, trabajadores, onClose }) {
  const esEntrada = tipo === 'entrada';
  const prefix    = esEntrada ? 'PE' : 'PS';
  const numero    = `${prefix}-${String(parte.numero).padStart(6, '0')}`;
  const fecha     = (parte.fecha_operacion || '').slice(0, 10);
  const detalle   = parte.detalle || [];
  const tienePrecios = !esEntrada && detalle.some(d => (parseFloat(d.precio_unitario) || 0) > 0);

  /* ── Firmas ── */
  const [recepciona, setRecepciona] = useState(() => esEntrada ? loadLS(LS.pe_recepciona, trabajadores) : null);
  const [autoriza,   setAutoriza]   = useState(() => !esEntrada ? loadLS(LS.ps_autoriza,  trabajadores) : null);
  const [despacha,   setDespacha]   = useState(() => !esEntrada ? loadLS(LS.ps_despacha,  trabajadores) : null);
  const [recibe,     setRecibe]     = useState(null);

  /* ── Logo de la empresa ── */
  const [logo, setLogo] = useState('');
  useEffect(() => {
    datosGeneralesService.get()
      .then(r => setLogo(r.data?.logo_base64 || ''))
      .catch(() => {});
  }, []);

  /* ── Inyectar CSS de impresión ── */
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'imprimir-parte-css';
    style.textContent = `
      @media print {
        body > * { visibility: hidden !important; }
        #imprimir-parte-content,
        #imprimir-parte-content * { visibility: visible !important; }
        #imprimir-parte-content {
          position: fixed !important;
          inset: 0 !important;
          width: 100vw !important;
          padding: 24px 36px !important;
          background: #fff !important;
          z-index: 99999 !important;
          box-sizing: border-box !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        #imprimir-parte-content table thead tr {
          background: #1e3a5f !important;
          color: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        #imprimir-parte-content table thead th {
          background: #1e3a5f !important;
          color: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        #imprimir-parte-content table tfoot tr {
          background: #e8eef4 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        #imprimir-parte-content table tbody tr:nth-child(odd) td {
          background: #f5f8fc !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .no-print { display: none !important; }
      }
    `;
    document.head.appendChild(style);
    return () => { const s = document.getElementById('imprimir-parte-css'); if (s) s.remove(); };
  }, []);

  const fmtN = (v, d = 4) => (parseFloat(v) || 0).toLocaleString('es-PE', { minimumFractionDigits: d, maximumFractionDigits: d });

  return (
    /* ── Overlay ── */
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1500,
      background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '24px 16px', overflowY: 'auto',
    }}>
      {/* Tarjeta modal */}
      <div style={{
        width: '100%', maxWidth: 900, background: '#0f1b2d',
        border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14,
        boxShadow: '0 24px 64px rgba(0,0,0,0.7)', overflow: 'hidden',
      }}>

        {/* ── Barra superior ── */}
        <div className="no-print" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(0,0,0,0.2)',
        }}>
          <div>
            <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>
              Vista previa de impresión
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#60a5fa', marginTop: 2 }}>{numero}</div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => window.print()} style={{
              display: 'flex', alignItems: 'center', gap: 7, padding: '7px 16px', fontSize: 13, fontWeight: 600,
              borderRadius: 8, border: '1px solid rgba(96,165,250,0.4)', cursor: 'pointer',
              background: 'rgba(96,165,250,0.12)', color: '#60a5fa', fontFamily: 'Inter, sans-serif',
              transition: 'all 0.15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(96,165,250,0.22)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(96,165,250,0.12)'; }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/>
              </svg>
              Imprimir
            </button>
            <button onClick={onClose} style={{
              padding: '7px 14px', fontSize: 13, fontWeight: 600, borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer',
              background: 'rgba(255,255,255,0.04)', color: '#94a3b8', fontFamily: 'Inter, sans-serif',
            }}>Cerrar</button>
          </div>
        </div>

        {/* ── Configuración de firmas ── */}
        <div className="no-print" style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: 11, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 12 }}>
            {esEntrada ? 'Firma de recepción' : 'Firmas del documento'}
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {esEntrada && (
              <FirmaAC label="Quien recepciona" trabajadores={trabajadores}
                value={recepciona} onChange={setRecepciona}
                canDefault lsKey={LS.pe_recepciona} />
            )}
            {!esEntrada && (<>
              <FirmaAC label="Quien autoriza" trabajadores={trabajadores}
                value={autoriza} onChange={setAutoriza}
                canDefault lsKey={LS.ps_autoriza} />
              <FirmaAC label="Quien despacha" trabajadores={trabajadores}
                value={despacha} onChange={setDespacha}
                canDefault lsKey={LS.ps_despacha} />
              <FirmaAC label="Quien recibe" trabajadores={trabajadores}
                value={recibe} onChange={setRecibe}
                canDefault={false} />
            </>)}
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════
            CONTENIDO IMPRIMIBLE
        ══════════════════════════════════════════════════════ */}
        <div id="imprimir-parte-content" style={{
          padding: '28px 36px', background: '#fff', color: '#1a1a2e',
          fontFamily: '"Segoe UI", Arial, sans-serif',
        }}>

          {/* ── Encabezado tipo membrete ── */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'stretch',
            marginBottom: 0, borderBottom: '3px solid #1e3a5f',
          }}>
            {/* Lado izquierdo: logo + empresa */}
            <div style={{ paddingBottom: 14, display: 'flex', alignItems: 'center', gap: 14 }}>
              {logo && (
                <img src={logo} alt="Logo" style={{ maxHeight: 64, maxWidth: 140, objectFit: 'contain' }} />
              )}
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, color: '#1e3a5f', letterSpacing: 0.5, marginBottom: 3 }}>
                  {parte.empresa_nombre || 'EMPRESA'}
                </div>
                {parte.ruc_empresa && (
                  <div style={{ fontSize: 10, color: '#555' }}>RUC: {parte.ruc_empresa}</div>
                )}
                {parte.sucursal_nombre && (
                  <div style={{ fontSize: 10, color: '#555' }}>Sucursal: {parte.sucursal_nombre}</div>
                )}
                {parte.almacen_nombre && (
                  <div style={{ fontSize: 10, color: '#555' }}>Almacén: {parte.almacen_nombre}</div>
                )}
              </div>
            </div>
            {/* Lado derecho: recuadro del número de documento */}
            <div style={{
              border: '2px solid #1e3a5f', borderBottom: 'none', borderRadius: '6px 6px 0 0',
              padding: '10px 24px', textAlign: 'center', minWidth: 200, alignSelf: 'flex-end',
              background: '#f0f4fa',
            }}>
              <div style={{ fontSize: 9, color: '#555', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: 700, marginBottom: 4 }}>
                {esEntrada ? 'Parte de Entrada' : 'Parte de Salida'}
              </div>
              <div style={{ fontSize: 24, fontWeight: 900, color: '#1e3a5f', letterSpacing: 2, fontFamily: 'Courier New, monospace' }}>
                {numero}
              </div>
              <div style={{ fontSize: 11, color: '#333', marginTop: 5, fontWeight: 600 }}>
                Fecha: {fecha}
              </div>
            </div>
          </div>

          {/* ── Ficha de datos (grid 2 columnas) ── */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr',
            border: '1px solid #b0bec5', borderTop: 'none',
            marginBottom: 16,
          }}>
            {/* Fila 1 */}
            {(parte.proveedor_nombre || parte.ruc_proveedor) && (
              <div style={{ padding: '5px 10px', borderRight: '1px solid #b0bec5', borderBottom: '1px solid #b0bec5', display: 'flex', gap: 6 }}>
                <span style={{ fontSize: 9, color: '#555', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', marginTop: 1 }}>Proveedor/Cliente:</span>
                <span style={{ fontSize: 10, color: '#1a1a2e', fontWeight: 600 }}>
                  {parte.ruc_proveedor ? `${parte.ruc_proveedor} — ` : ''}{parte.proveedor_nombre || ''}
                </span>
              </div>
            )}
            {parte.solicitante_nombre && (
              <div style={{ padding: '5px 10px', borderBottom: '1px solid #b0bec5', display: 'flex', gap: 6 }}>
                <span style={{ fontSize: 9, color: '#555', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', marginTop: 1 }}>Solicitante:</span>
                <span style={{ fontSize: 10, color: '#1a1a2e', fontWeight: 600 }}>{parte.solicitante_nombre}</span>
              </div>
            )}
            {/* Tipo movimiento */}
            {parte.tipo_movimiento_codigo && (
              <div style={{ padding: '5px 10px', borderRight: '1px solid #b0bec5', borderBottom: '1px solid #b0bec5', display: 'flex', gap: 6 }}>
                <span style={{ fontSize: 9, color: '#555', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', marginTop: 1 }}>Tipo Movimiento:</span>
                <span style={{ fontSize: 10, color: '#1a1a2e', fontWeight: 600 }}>[{parte.tipo_movimiento_codigo}] {parte.tipo_movimiento_descripcion || ''}</span>
              </div>
            )}
            {/* Centro de costo — siempre visible */}
            <div style={{ padding: '5px 10px', borderBottom: '1px solid #b0bec5', display: 'flex', gap: 6 }}>
              <span style={{ fontSize: 9, color: '#555', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', marginTop: 1 }}>Centro de Costo:</span>
              <span style={{ fontSize: 10, color: parte.centro_costo_descripcion ? '#1a1a2e' : '#999', fontWeight: parte.centro_costo_descripcion ? 600 : 400, fontStyle: parte.centro_costo_descripcion ? 'normal' : 'italic' }}>
                {parte.centro_costo_descripcion
                  ? `[${parte.centro_costo_codigo || ''}] ${parte.centro_costo_descripcion}`
                  : 'Sin centro de costo'}
              </span>
            </div>
            {/* Refs */}
            {parte.num_doc_ref1 && (
              <div style={{ padding: '5px 10px', borderBottom: '1px solid #b0bec5', display: 'flex', gap: 6 }}>
                <span style={{ fontSize: 9, color: '#555', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', marginTop: 1 }}>{parte.tipo_doc_ref1_descripcion || 'Ref. 1'}:</span>
                <span style={{ fontSize: 10, color: '#1a1a2e', fontWeight: 600 }}>{parte.num_doc_ref1}</span>
              </div>
            )}
            {parte.num_doc_ref2 && (
              <div style={{ padding: '5px 10px', borderRight: '1px solid #b0bec5', borderBottom: '1px solid #b0bec5', display: 'flex', gap: 6 }}>
                <span style={{ fontSize: 9, color: '#555', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', marginTop: 1 }}>{parte.tipo_doc_ref2_descripcion || 'Ref. 2'}:</span>
                <span style={{ fontSize: 10, color: '#1a1a2e', fontWeight: 600 }}>{parte.num_doc_ref2}</span>
              </div>
            )}
            {/* Observaciones — ocupa todo el ancho */}
            {parte.observacion && (
              <div style={{ padding: '5px 10px', gridColumn: '1 / -1', display: 'flex', gap: 6, borderBottom: '1px solid #b0bec5' }}>
                <span style={{ fontSize: 9, color: '#555', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', marginTop: 1 }}>Observaciones:</span>
                <span style={{ fontSize: 10, color: '#1a1a2e' }}>{parte.observacion}</span>
              </div>
            )}
          </div>

          {/* ── Tabla de artículos estilo Excel ── */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10.5, marginBottom: 28, border: '1.5px solid #37474f' }}>
            <thead>
              <tr style={{ background: '#1e3a5f', color: '#fff' }}>
                <th style={{ padding: '7px 6px', textAlign: 'center', width: 26, border: '1px solid #2d5a8e', fontSize: 9, fontWeight: 700, letterSpacing: '0.5px' }}>#</th>
                <th style={{ padding: '7px 8px', textAlign: 'left', width: 88, border: '1px solid #2d5a8e', fontSize: 9, fontWeight: 700, letterSpacing: '0.5px' }}>CÓDIGO</th>
                <th style={{ padding: '7px 8px', textAlign: 'left', border: '1px solid #2d5a8e', fontSize: 9, fontWeight: 700, letterSpacing: '0.5px' }}>DESCRIPCIÓN</th>
                <th style={{ padding: '7px 6px', textAlign: 'center', width: 48, border: '1px solid #2d5a8e', fontSize: 9, fontWeight: 700, letterSpacing: '0.5px' }}>U.M.</th>
                <th style={{ padding: '7px 8px', textAlign: 'right', width: 80, border: '1px solid #2d5a8e', fontSize: 9, fontWeight: 700, letterSpacing: '0.5px' }}>CANTIDAD</th>
                {tienePrecios && <>
                  <th style={{ padding: '7px 8px', textAlign: 'right', width: 78, border: '1px solid #2d5a8e', fontSize: 9, fontWeight: 700, letterSpacing: '0.5px' }}>P. UNIT.</th>
                  <th style={{ padding: '7px 6px', textAlign: 'center', width: 46, border: '1px solid #2d5a8e', fontSize: 9, fontWeight: 700, letterSpacing: '0.5px' }}>IGV%</th>
                  <th style={{ padding: '7px 8px', textAlign: 'right', width: 82, border: '1px solid #2d5a8e', fontSize: 9, fontWeight: 700, letterSpacing: '0.5px' }}>TOTAL</th>
                </>}
              </tr>
            </thead>
            <tbody>
              {detalle.map((it, i) => {
                const pu    = parseFloat(it.precio_unitario) || 0;
                const pct   = parseFloat(it.igv_pct) || 0;
                const total = (pu + pu * pct / 100) * (parseFloat(it.cantidad) || 0);
                const bg    = i % 2 === 0 ? '#f5f8fc' : '#fff';
                const border = '1px solid #cfd8dc';
                return (
                  <tr key={i} style={{ background: bg }}>
                    <td style={{ padding: '5px 6px', textAlign: 'center', color: '#666', border, fontSize: 10 }}>{i + 1}</td>
                    <td style={{ padding: '5px 8px', fontFamily: 'Courier New, monospace', fontSize: 9.5, color: '#37474f', border }}>{it.articulo_codigo || '—'}</td>
                    <td style={{ padding: '5px 8px', border, fontWeight: 500, color: '#1a1a2e' }}>{it.articulo_descripcion || '—'}</td>
                    <td style={{ padding: '5px 6px', textAlign: 'center', color: '#444', border }}>{it.unidad_codigo || it.unidad_descripcion || '—'}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: 'Courier New, monospace', border, fontWeight: 600 }}>{fmtN(it.cantidad, 4)}</td>
                    {tienePrecios && <>
                      <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: 'Courier New, monospace', border }}>{fmtN(pu, 2)}</td>
                      <td style={{ padding: '5px 6px', textAlign: 'center', color: '#555', border }}>{pct}%</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: 'Courier New, monospace', border, fontWeight: 700, color: '#1e3a5f' }}>{fmtN(total, 2)}</td>
                    </>}
                  </tr>
                );
              })}
              {/* Fila vacía de cierre si hay pocos items */}
              {detalle.length < 5 && Array.from({ length: 5 - detalle.length }).map((_, i) => (
                <tr key={`empty-${i}`} style={{ background: i % 2 === 0 ? '#f5f8fc' : '#fff' }}>
                  <td style={{ padding: '5px 6px', border: '1px solid #cfd8dc', color: 'transparent' }}>·</td>
                  <td style={{ border: '1px solid #cfd8dc' }} />
                  <td style={{ border: '1px solid #cfd8dc' }} />
                  <td style={{ border: '1px solid #cfd8dc' }} />
                  <td style={{ border: '1px solid #cfd8dc' }} />
                  {tienePrecios && <><td style={{ border: '1px solid #cfd8dc' }} /><td style={{ border: '1px solid #cfd8dc' }} /><td style={{ border: '1px solid #cfd8dc' }} /></>}
                </tr>
              ))}
            </tbody>
            {tienePrecios && (
              <tfoot>
                <tr style={{ background: '#e8eef4' }}>
                  <td colSpan={tienePrecios ? 7 : 4} style={{ padding: '6px 10px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: '#1e3a5f', border: '1.5px solid #37474f', letterSpacing: '0.5px' }}>
                    TOTAL GENERAL:
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: 800, color: '#1e3a5f', border: '1.5px solid #37474f', fontSize: 11 }}>
                    {fmtN(detalle.reduce((acc, it) => {
                      const pu = parseFloat(it.precio_unitario) || 0;
                      const pct = parseFloat(it.igv_pct) || 0;
                      return acc + (pu + pu * pct / 100) * (parseFloat(it.cantidad) || 0);
                    }, 0), 2)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>

          {/* ── Firmas ── */}
          <div style={{ display: 'flex', gap: 0, marginTop: 16, pageBreakInside: 'avoid', borderTop: '1.5px solid #b0bec5', paddingTop: 8 }}>
            {esEntrada ? (
              <FirmaBox titulo="Recepciona" trabajador={recepciona} />
            ) : (<>
              <FirmaBox titulo="Autoriza"   trabajador={autoriza}   />
              <FirmaBox titulo="Despacha"   trabajador={despacha}   />
              <FirmaBox titulo="Recibe"     trabajador={recibe}     />
            </>)}
          </div>

          {/* ── Pie de página ── */}
          <div style={{ marginTop: 20, borderTop: '1px solid #e0e0e0', paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontSize: 8.5, color: '#999' }}>
            <span>Documento generado por el sistema — {new Date().toLocaleString('es-PE')}</span>
            <span>{numero}</span>
          </div>

        </div>{/* /imprimir-parte-content */}
      </div>
    </div>
  );
}
